import { useLocation, useNavigate, useSearchParams } from 'react-router';

import { authenticatedJsonFetch } from '@perpay-web/authentication/authenticatedJsonFetch';
import { authentication } from '@perpay-web/authentication/singleton';
import { HTTPMethod } from '@perpay-web/constants/httpMethods';
import { replaceAuthTokens } from '@perpay-web/fintech/actions/authentication';
import { PayCycleType } from '@perpay-web/fintech/constants/payCycle';
import { PAYCHEX_SSO_FLOW_CONFIG_MAP } from '@perpay-web/fintech/constants/paychexSSOFlow';
import {
    PAYCHEX_CONFIRM_EMPLOYMENT_ENDPOINT,
    PAYCHEX_EMPLOYMENT_INFO_ENDPOINT,
    PAYCHEX_LOGIN_ENDPOINT,
    PAYCHEX_SSO_URL_ENDPOINT,
} from '@perpay-web/fintech/constants/urls';
import { useAppDispatch } from '@perpay-web/fintech/hooks';
import { useHasSpendingLimit } from '@perpay-web/fintech/hooks/useSpendingLimit';
import { paths } from '@perpay-web/fintech/props/appPaths';
import { PAYCHEX_SSO_FLOW_TYPE } from '@perpay-web/fintech/types/payrollProviderTypes';
import { toEnumWithDefault } from '@perpay-web/fintech/utils/enumUtils';
import { dollarToCents } from '@perpay-web/utils/numberUtils';
import { decodeTokens, storeToken } from '@perpay-web/utils/tokenUtils';

export type PaychexCompany = {
    companyName: string;
    companyUuid: string;
    paycheckAmount: number;
    payCycle: PayCycleType;
};

export const loginPaychex = async (code: string) => {
    const params = new URLSearchParams({
        code,
    });
    return authenticatedJsonFetch<{ connectionUuid: string }>(
        `${PAYCHEX_LOGIN_ENDPOINT}?${params}`,
    );
};

export const fetchPaychexCompanyData = async () =>
    authenticatedJsonFetch<PaychexCompany[]>(PAYCHEX_EMPLOYMENT_INFO_ENDPOINT);

const submitPaychexCompanyData = async (data: PaychexCompany) =>
    authenticatedJsonFetch<{
        tokens: {
            access: string;
            refresh: string;
        };
    }>(
        PAYCHEX_CONFIRM_EMPLOYMENT_ENDPOINT,
        {
            companyName: data.companyName,
            companyUuid: data.companyUuid,
            paycheckAmount: dollarToCents(data.paycheckAmount),
            payCycle: data.payCycle,
        },
        HTTPMethod.post,
    );

export const useSubmitPaychexCompanyData = () => {
    const dispatch = useAppDispatch();

    return async (data: PaychexCompany) => {
        const { tokens } = await submitPaychexCompanyData(data);
        const decodedTokens = decodeTokens(tokens);
        storeToken(decodedTokens);
        dispatch(replaceAuthTokens(decodedTokens));
        await authentication.refresh();
    };
};

export const getPaychexLoginURL = async (flowType: PAYCHEX_SSO_FLOW_TYPE) =>
    authenticatedJsonFetch<{
        url: string;
        state: string;
    }>(
        PAYCHEX_SSO_URL_ENDPOINT,
        {
            flowType,
        },
        HTTPMethod.post,
    );

export const useParsePaychexState = () => {
    const [searchParams] = useSearchParams();
    const existingUser = useHasSpendingLimit();
    const state = searchParams.get('state') || '';
    try {
        const { flowType } = JSON.parse(atob(decodeURIComponent(state))) as {
            flowType: PAYCHEX_SSO_FLOW_TYPE;
        };
        return toEnumWithDefault(
            PAYCHEX_SSO_FLOW_TYPE,
            flowType,
            existingUser
                ? PAYCHEX_SSO_FLOW_TYPE.SIGNUP_EXISTING_USER
                : PAYCHEX_SSO_FLOW_TYPE.SIGNUP_NEW_USER,
        );
    } catch (unusedError) {
        return existingUser
            ? PAYCHEX_SSO_FLOW_TYPE.SIGNUP_EXISTING_USER
            : PAYCHEX_SSO_FLOW_TYPE.SIGNUP_NEW_USER;
    }
};

export const usePaychexFlowType = () => {
    const location = useLocation();

    const existingUser = useHasSpendingLimit();
    const flowType = toEnumWithDefault(
        PAYCHEX_SSO_FLOW_TYPE,
        location.state?.flowType,
        existingUser
            ? PAYCHEX_SSO_FLOW_TYPE.SIGNUP_EXISTING_USER
            : PAYCHEX_SSO_FLOW_TYPE.SIGNUP_NEW_USER,
    );

    return {
        flowType,
        flowConfig: PAYCHEX_SSO_FLOW_CONFIG_MAP[flowType],
    };
};

export const useStartPaychexSSOFlow = (flowType: PAYCHEX_SSO_FLOW_TYPE) => {
    const navigate = useNavigate();
    const flowConfig = PAYCHEX_SSO_FLOW_CONFIG_MAP[flowType];
    const path = flowConfig.introScreen
        ? paths.payrollConnectionIntro.path
        : paths.paychexSSORedirect.path;

    return () => {
        navigate(path, {
            state: {
                flowType,
            },
        });
    };
};

export const getPaychexLoginUrl = (): string =>
    `${paths.paychexLogin.path}?${new URLSearchParams({
        next: paths.paychexSSORedirect.path,
    })}`;
